import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaSearch, FaCalendar, FaUser, FaArrowRight, FaEye } from 'react-icons/fa';
import axios from 'axios';
import { fetchBlogs } from '../redux/slices/blogSlice';
import { API_URL } from '../config/apiConfig';

const CATEGORIES = ['All', 'Fitness', 'Training', 'Nutrition', 'Wellness', 'Lifestyle'];

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

const BlogPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { blogs, loading, error } = useSelector((state) => state.blog);

  const [activeCategory, setActiveCategory] = useState('All');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  const [newsletterError, setNewsletterError] = useState('');

  useEffect(() => {
    dispatch(fetchBlogs({
      category: activeCategory !== 'All' ? activeCategory : undefined,
      search: searchQuery || undefined,
      limit: 13,
    }));
  }, [dispatch, activeCategory, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    setSearchInput('');
    setSearchQuery('');
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);
    setNewsletterError('');
    try {
      await axios.post(`${API_URL}/newsletter/subscribe`, {
        email: newsletterEmail,
        name: newsletterName,
        source: 'website_blog',
      });
      setNewsletterSuccess(true);
      setNewsletterEmail('');
      setNewsletterName('');
      setTimeout(() => setNewsletterSuccess(false), 5000);
    } catch (err) {
      setNewsletterError(err.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const featuredPost = blogs[0] || null;
  const gridPosts = blogs.slice(1);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-sm font-semibold mb-6">
              MYFITNESS BLOG
            </div>
            <h1 className="text-5xl md:text-7xl font-black mb-6">
              Fitness <span className="text-primary">Knowledge</span> Hub
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10">
              Expert tips, training guides, nutrition advice, and wellness strategies to fuel your fitness journey.
            </p>
            <form onSubmit={handleSearch} className="max-w-xl mx-auto flex gap-3">
              <div className="flex-1 relative">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search articles..."
                  className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary backdrop-blur-sm"
                />
              </div>
              <button
                type="submit"
                className="bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-semibold transition-colors"
              >
                Search
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-3 overflow-x-auto pb-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => handleCategoryChange(cat)}
                className={`flex-shrink-0 px-5 py-2 rounded-full font-semibold text-sm transition-all ${
                  activeCategory === cat
                    ? 'bg-primary text-white'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Loading */}
      {loading && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-400 mt-4">Loading articles...</p>
          </div>
        </section>
      )}

      {/* Error */}
      {error && !loading && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-red-400 text-lg mb-4">{error}</p>
            <button
              onClick={() => dispatch(fetchBlogs({ category: activeCategory !== 'All' ? activeCategory : undefined }))}
              className="bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-semibold transition-colors"
            >
              Try Again
            </button>
          </div>
        </section>
      )}

      {/* Empty State */}
      {!loading && !error && blogs.length === 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-400 text-lg">
              No articles found
              {searchQuery ? ` for "${searchQuery}"` : ''}
              {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}.
            </p>
            {(searchQuery || activeCategory !== 'All') && (
              <button
                onClick={() => { setSearchInput(''); setSearchQuery(''); setActiveCategory('All'); }}
                className="mt-4 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-semibold transition-colors"
              >
                Clear Filters
              </button>
            )}
          </div>
        </section>
      )}

      {/* Featured Post */}
      {!loading && !error && featuredPost && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-8 flex items-center gap-3">
              <span className="text-primary">★</span> Featured Article
            </h2>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => navigate(`/blog/${featuredPost.slug}`)}
              className="cursor-pointer glass rounded-3xl overflow-hidden group hover:scale-[1.01] transition-transform"
            >
              <div className="grid md:grid-cols-2">
                <div className="relative h-72 md:h-auto overflow-hidden">
                  {featuredPost.featuredImage?.url ? (
                    <img
                      src={featuredPost.featuredImage.url}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full min-h-72 bg-gradient-to-br from-primary/30 to-gray-800 flex items-center justify-center">
                      <span className="text-8xl opacity-10">💪</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-black/20" />
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center">
                  <div className="inline-block bg-primary/20 text-primary px-4 py-1 rounded-full text-sm font-semibold mb-4 self-start">
                    {featuredPost.category}
                  </div>
                  <h3 className="text-3xl font-black mb-4 group-hover:text-primary transition-colors leading-tight">
                    {featuredPost.title}
                  </h3>
                  <p className="text-gray-400 mb-6 line-clamp-3">{featuredPost.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-5 text-sm text-gray-400 mb-6">
                    <div className="flex items-center gap-2">
                      <FaUser className="text-primary" />
                      <span>{featuredPost.author?.fullName || 'MyFitness Team'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FaCalendar className="text-primary" />
                      <span>{formatDate(featuredPost.publishedAt || featuredPost.createdAt)}</span>
                    </div>
                    {featuredPost.views > 0 && (
                      <div className="flex items-center gap-2">
                        <FaEye className="text-primary" />
                        <span>{featuredPost.views.toLocaleString()} views</span>
                      </div>
                    )}
                  </div>
                  <span className="flex items-center gap-2 text-primary font-semibold group-hover:gap-4 transition-all">
                    Read Article <FaArrowRight />
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Grid Posts */}
      {!loading && !error && gridPosts.length > 0 && (
        <section className="pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold mb-8">All Articles</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {gridPosts.map((post, index) => (
                <motion.div
                  key={post._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => navigate(`/blog/${post.slug}`)}
                  className="glass rounded-2xl overflow-hidden cursor-pointer group hover:scale-105 transition-transform"
                >
                  <div className="relative h-48 overflow-hidden">
                    {post.featuredImage?.url ? (
                      <img
                        src={post.featuredImage.url}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/30 to-gray-800 flex items-center justify-center">
                        <span className="text-5xl opacity-10">💪</span>
                      </div>
                    )}
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary/80 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 line-clamp-3">{post.excerpt}</p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-400">
                      <div className="flex items-center gap-1">
                        <FaUser />
                        <span>{post.author?.fullName || 'MyFitness Team'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <FaCalendar />
                        <span>{formatDate(post.publishedAt || post.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-block bg-primary/20 text-primary px-6 py-2 rounded-full text-sm font-semibold mb-6">
              NEWSLETTER
            </div>
            <h2 className="text-4xl font-black mb-4">
              Stay in the <span className="text-primary">Loop</span>
            </h2>
            <p className="text-gray-400 mb-10">
              Get the latest fitness tips, expert advice, and exclusive content delivered straight to your inbox.
            </p>

            {newsletterSuccess ? (
              <div className="bg-green-500/20 border border-green-500/30 text-green-400 rounded-2xl p-6">
                <p className="text-lg font-semibold">You're subscribed!</p>
                <p className="text-sm mt-1">Check your email for a welcome message from the MyFitness team.</p>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="text"
                    value={newsletterName}
                    onChange={(e) => setNewsletterName(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary backdrop-blur-sm"
                  />
                  <input
                    type="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="flex-1 px-5 py-4 bg-white/10 border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-primary backdrop-blur-sm"
                  />
                </div>
                {newsletterError && (
                  <p className="text-red-400 text-sm">{newsletterError}</p>
                )}
                <button
                  type="submit"
                  disabled={newsletterLoading}
                  className="w-full bg-primary hover:bg-primary/90 disabled:opacity-50 py-4 rounded-xl font-bold text-white transition-colors"
                >
                  {newsletterLoading ? 'Subscribing...' : 'Subscribe to Newsletter'}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPage;
